import Image from "next/image";
import { toValidDays } from "@/hooks/useWelcomeCouponIssuing";
import type { WelcomeCouponOffer } from "@/libs/welcome-coupon/getWelcomeOffer";
import { AuthUtils } from "@/utils/AuthUtils";
import { useTrans } from "@/utils/useTrans";

/**
 * 봉투 + 쿠폰 티켓 그래픽 (시안 `30869:157881`, 원본 1055×1044).
 *
 * ⚠️ **퍼센트는 이 이미지에 없다.** 시안도 `5%` 를 별도 텍스트 레이어로 분리해 뒀다
 * (`30869:157882`). 서버가 내려주는 `offer.percent` 를 그려야 하므로, 숫자를 픽셀로
 * 굽는 순간 캠페인마다 이미지를 다시 뽑아야 한다 — 이 기능이 없애려던 바로 그 미러다.
 */
const WELCOME_COUPON_TICKET = "/images/coupon/welcome-coupon-ticket.webp";

/** 원본 비율 — 파일이 조용히 교체되는 것을 가드가 잡는다(GTA5 배경과 같은 관례). */
export const WELCOME_COUPON_TICKET_RATIO = 1055 / 1044;

/**
 * 웰컴 쿠폰 **카드 본문** — `FloatingPopup` 의 슬라이드로 들어간다.
 *
 * ## ★종전엔 자기 팝업이었다 (2026-09-11 이관)
 *
 * ~~`welcome-coupon-popup.tsx`~~ 가 `fixed z-50 … laptop:bottom-6 laptop:right-6` 로
 * **따로** 떠 있었고, GTA5 캠페인 팝업(`FloatingPopup`)이 **완전히 같은 자리·같은
 * `z-50`** 을 쓴다. 두 노출 조건이 서로를 모르므로(양방향 grep 0건) 겹쳤다. 그래서
 * 캐러셀 **한 껍데기**로 합쳤다(`home-floating-popups.tsx`).
 *
 * 그때 걷은 것 둘: ①바깥 `fixed` 래퍼(껍데기가 자리를 잡는다) ②자기 하단 바
 * (껍데기의 공용 바가 「오늘 안 보기」·「닫기」를 든다 — 그대로 두면 바가 두 줄이 된다).
 * 공용 바의 「오늘 안 보기」는 조합기가 `onDismissToday` 로 종전과 **같은 `dismiss()`**
 * 에 연결하므로 #5115 계약(팝업·배지 두 표면 모두에 걸린다)이 유지된다.
 *
 * ## ★2026-09-14 — 높이를 형제 슬라이드와 맞췄다 (시안 `30835:157744`)
 *
 * 종전 이 카드는 세로로 긴 노란 카드(실측 283px)였는데, 형제인 GTA5 슬라이드는
 * **190px** 였다. 껍데기가 활성 슬라이드를 재서 높이를 맞추므로(`floating-popup` 의
 * `shellHeight`), 두 장이 달라 **전환할 때마다 창이 늘었다 줄었고** 그 사이 내용이
 * 뷰포트를 넘겨 **스크롤바가 잠깐 떴다**(사용자 제보 2026-09-14).
 *
 * ```
 *   종전   GTA5 190  ↔  쿠폰 283      차이 93 → 전환마다 높이 애니메이션 + 스크롤
 *   지금   GTA5 190  ↔  쿠폰 190      차이 0  → 껍데기가 잴 값이 안 바뀐다
 * ```
 *
 * ⇒ **껍데기는 건드리지 않았다.** 재는 machinery 는 이미지가 늦게 로드될 때를 위한
 * 것이라 그대로 두는 편이 안전하고, 두 슬라이드가 같은 높이면 잴 값이 상수가 된다.
 * 고칠 자리는 셸이 아니라 **어긋나 있던 이 카드**였다.
 *
 * ⚠️ 그래서 이 밴드의 `h-[190px]` 는 **형제와 맞춘 계약**이다. 바꾸려면 GTA5 슬라이드
 * (`gta5-giveaway-popup.tsx` 의 `Gta5Slide`)도 같이 바꿔야 한다 — 한쪽만 바꾸면
 * 위 증상이 그대로 돌아온다.
 *
 * ⚠️ **딤은 원래도 없었고 지금도 없다** — 그 팝업의 "딤 없이 띄운다(기획 확정)" 가
 * 이 템플릿을 재사용하지 않은 이유였다.
 *
 * ⚠️ 노출 판정은 여기 없다 — `useWelcomeCouponOffer` 한 곳이고 미니 배지와 공유한다.
 * 이 컴포넌트는 `offer` 를 받아 **그리기만** 한다.
 */
export default function WelcomeCouponSlide({
  offer,
}: {
  offer: WelcomeCouponOffer;
}) {
  const { t } = useTrans(["gnb"]);
  const validDays = toValidDays(offer.validForHours);

  return (
    /**
     * 밴드 높이·배경은 시안 `30835:157746` — 그라디언트는 토큰이 없어 raw hex 를 쓴다.
     * 아래 모서리를 둥글리지 않는 이유: 카드 밑에 껍데기의 공용 바가 붙으므로 각져야
     * 붙어 보인다(위 라운드도 껍데기가 바깥에서 깎는다).
     */
    <div className="relative flex h-[190px] items-center overflow-hidden bg-gradient-to-b from-[#453E18] to-[#292407] p-4">
      {/**
       * ★**데스크탑에서만 왼쪽으로 민다 — 가로는 카드 안, 세로는 넘치게 둔다**
       * (작성자 확정 2026-09-14).
       *
       * ```
       *                     left    우측 끝            밴드 폭   결과
       *   시안·모바일(기본)  199.32  410.32            375~     그대로 둔다
       *   데스크탑(md+)      149     360      =        360      경계에 딱 맞춘다
       *   세로(공통)         top 17.76 + h 208 = 225.76 > 190   그대로 둔다
       * ```
       *
       * 시안(`30835:157744`)은 `left 199.32` 로 카드를 가로 약 50px 넘긴다. 시안 자체는
       * `front-card` 의 `overflow-clip` 에 잘린 모양이 완성형이지만, **캐러셀에서는 그 잘린 면이
       * 슬라이드 경계**라 두 가지가 걸렸다.
       *
       * ① 잘리는 대상이 봉투 몸통이 아니라 `COUPON` **글자 끝**이라 "짤린 글자"로 보인다.
       * ② 트랙 안에서는 슬라이드가 가로로 나란히 놓여 있어, 넘친 50px 이 **이웃(GTA5) 셀 위에
       *    그려진다** — 전환 중 겹치고 전환 뒤에도 조각이 남았다(마케팅 제보 2026-09-14).
       *
       * ⇒ **데스크탑 카드(360 고정)에서만** 우측 끝을 카드 경계에 맞췄다. 크기는 시안 그대로다.
       *
       * ⚠️ **기본값(모바일)은 건드리지 않았다.** 모바일 시트는 폭이 화면을 따라가므로
       * (375 에서 375, 820 에서 805) 카드 기준 고정값을 옮기면 화면 크기마다 여백이 달라진다.
       * 지금 상태가 최적이라는 판단이라 `md:` 한 단계로만 갈랐다 — 껍데기의 두 표면도 같은
       * `md` 에서 갈린다(`floating-popup` 의 `md:flex` / `md:hidden`, 이 레포 `md` = 1024).
       *
       * ⚠️ 더 왼쪽으로 밀면 안 된다 — 글자 컬럼이 `16..176` 을 쓴다(149 부터 겹치지만 그 구간은
       * 봉투의 투명 여백이라 문구·CTA 를 가리지 않는다. 실측 확인).
       *
       * 🔴 세로 넘침은 손대지 않았다(화면 하단에서 잘리는 건 원래도 그랬고 어색하지 않다).
       * 그래서 **밴드 루트의 `overflow-hidden` 이 계속 필요하다** — 없으면 그 35.75px 이 아래
       * 이웃·하단 바로 번지고, 좌표가 다시 틀어지면 가로 침범도 즉시 되살아난다. 껍데기에 맡길 수
       * 없다(껍데기의 `overflow-hidden` 은 **카드 바깥** 경계만 깎는다). 트랙 셀(`CarouselItem`)
       * 에도 걸 수 없다 — `floating-popup` 주석이 금지한다(배경이 셀 경계에 잘리고 라운드가
       * 두 겹이 된다). **자기 밴드에서 자른다.**
       */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-[199.32px] top-[17.76px] h-[208px] w-[211px] md:left-[149px]"
      >
        <Image
          src={WELCOME_COUPON_TICKET}
          alt=""
          fill
          sizes="211px"
          className="object-cover"
          priority
        />
        {/**
         * 퍼센트는 **서버 값**이라 이미지가 아니라 텍스트로 올린다(시안도 분리했다).
         * 좌표는 티켓 박스 기준 상대값 — 시안의 밴드 기준 `(263.32, 54.76)` 에서
         * 티켓 원점 `(199.32, 17.76)` 을 뺀 값이다.
         *
         * ⚠️ `font-[800]` 은 임의값이다 — 이 레포 `fontWeight` 토큰은 400·500·600·700
         * 뿐이라 `font-800` 은 **무효 클래스**가 된다(docs/pitfalls.md "font-bold·
         * font-medium 은 무효 클래스"). 시안이 ExtraBold 라 임의값으로 준다.
         */}
        <span className="absolute left-[64px] top-[37px] flex h-[81px] w-[108px] rotate-[-22.22deg] items-center justify-center whitespace-nowrap text-[64px] font-[800] leading-[48px] tracking-[0.1px] text-[#F5F5F3] [text-shadow:0_2px_2px_#EFB009]">
          {`${offer.percent}%`}
        </span>
      </div>

      {/**
       * ★**폭 160 을 고정한다** — 오른쪽에 티켓 그래픽이 있어 글자가 거기까지 가면
       * 겹친다. 시안이 `w-[160px]` 로 명시한다(GTA5 는 인물 때문에 180).
       */}
      <div className="relative flex w-[160px] flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-base font-600 leading-[18px] tracking-[0.25px] text-white">
            {t("New member coupon")}
          </p>
          {/**
           * 유효기간은 1일 이상일 때만 적는다 — 24시간 미만 캠페인에서 "0일" 이나
           * 과장된 "1일" 을 고지하지 않기 위해서다(`toValidDays` 주석 참조).
           * 별표는 시안의 장식이라 번역 키 밖에 둔다(7 로케일 문구를 안 건드린다).
           */}
          {validDays !== null && (
            <p className="text-2xs font-400 leading-[14px] tracking-[0.4px] text-green-30">
              {`*${t("Use within {{days}} days", { days: validDays })}`}
            </p>
          )}
        </div>

        <a
          // 가입 유도 CTA 이므로 로그인이 아니라 계정 만들기 화면으로 보낸다. 이미 계정이
          // 있는 사용자는 signup 화면 하단 "로그인" 링크로 빠질 수 있다(success-url 유지).
          href={AuthUtils.getSignUpUrl()}
          className="flex h-10 w-fit items-center justify-center rounded-[44px] bg-bhaptics-yellow px-6 text-sm font-600 leading-4 tracking-[0.1px] text-black transition-colors hover:bg-bhaptics-yellow-hover"
        >
          {/* 이미 7 로케일에 있는 배지 키를 그대로 쓴다 — 같은 문장을 두 벌로 만들지 않는다. */}
          {t("Sign Up for {{percent}}% Off", { percent: offer.percent })}
        </a>
      </div>
    </div>
  );
}
